'use client'

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User } from '@supabase/supabase-js'
import type { UserWithoutSalary } from '@buildbox/shared/types'
import { fetchUserFromServerAction } from '@/app/actions/user.actions'
import type { ForcedLogoutReason } from '@/lib/auth/forced-logout'

export type { ForcedLogoutReason }

interface UserState {
	supabaseUser: User | null
	databaseUser: UserWithoutSalary | null
	setUser: (user: User | null) => void
	setDatabaseUser: (databaseUser: UserWithoutSalary | null) => void
	clearUser: () => void
	refreshDatabaseUser: () => Promise<UserWithoutSalary | null>
	forcedLogoutReason: ForcedLogoutReason | null
	forcedLogoutToastPending: boolean
	forcedLogoutEventId: number
	forcedLogoutToastToken: number | null
	setForcedLogoutNotice: (reason: ForcedLogoutReason) => void
	acknowledgeForcedLogoutNotice: () => void
	clearForcedLogoutNotice: () => void
}

export const useUserStore = create<UserState>()(
	persist(
		(set, get) => ({
			supabaseUser: null,
			databaseUser: null,
			setUser: (user) => set({ supabaseUser: user }),
			setDatabaseUser: (databaseUser) => set({ databaseUser }),
			clearUser: () => set({ supabaseUser: null, databaseUser: null }),
			refreshDatabaseUser: async () => {
				const { supabaseUser } = get()
				if (supabaseUser?.id) {
					const databaseUserData = await fetchUserFromServerAction(supabaseUser.id)
					set({ databaseUser: databaseUserData ?? null })
					return databaseUserData ?? null
				}
				set({ databaseUser: null })
				return null
			},
			forcedLogoutReason: null,
			forcedLogoutToastPending: false,
			forcedLogoutEventId: 0,
			forcedLogoutToastToken: null,
			setForcedLogoutNotice: (reason) =>
				set((state) => {
					const nextId = state.forcedLogoutEventId + 1
					return {
						forcedLogoutReason: reason,
						forcedLogoutToastPending: true,
						forcedLogoutEventId: nextId,
						forcedLogoutToastToken: nextId,
					}
				}),
			acknowledgeForcedLogoutNotice: () =>
				set({ forcedLogoutReason: null, forcedLogoutToastPending: false, forcedLogoutToastToken: null }),
			clearForcedLogoutNotice: () =>
				set({ forcedLogoutReason: null, forcedLogoutToastPending: false, forcedLogoutToastToken: null }),
		}),
		{
			name: 'user-storage',
			partialize: (state) => ({
				supabaseUser: state.supabaseUser,
				databaseUser: state.databaseUser,
				forcedLogoutReason: state.forcedLogoutReason,
				forcedLogoutToastPending: state.forcedLogoutToastPending,
				forcedLogoutEventId: state.forcedLogoutEventId,
				forcedLogoutToastToken: state.forcedLogoutToastToken,
			}),
		},
	),
)
